import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { handleWrongPlatformError } from '../../utils/wrongPlatform';
import { buildAuth0ShiftRedirectUrl, persistAuth0Grant } from '../../utils/auth0Session';

interface LoginModalProps {
  header: string;
  para: any;
  isOpen: boolean;
  object: any;
  setObject: any;
  onClose: () => void;
  onChange: () => void;
  onLogin: (status: boolean) => void;
  loader: boolean;
  onBackToLogin?: () => void;
}

const Login: React.FC<LoginModalProps> = ({
  header,
  para,
  isOpen,
  onClose,
  onChange,
  onLogin,
  setObject,
  object,
  loader,
  onBackToLogin,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState({
    label: '',
    color: '',
  });
  const [criteria, setCriteria] = useState({
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleFormSubmit = async () => {
    const redirect_uri = `${window.location.origin}/shift?defaultCurrency=${object?.defaultCurrency}`;
    if (!object) {
      setError('Form data is not properly initialized.');
      return;
    }

    if (!object.email?.trim() || !object.password?.trim() || !object.confirmPassword?.trim()) {
      setError('All fields are required.');
      return;
    }

    if (object.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    // Password strength validation
    if (
      !criteria.hasLowercase ||
      !criteria.hasUppercase ||
      !criteria.hasNumber ||
      !criteria.hasSpecialChar
    ) {
      setError(
        'Password must meet all strength criteria (lowercase, uppercase, number, and special character).'
      );
      return;
    }

    if (object.password !== object.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(object.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      onLogin(true);

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URI}/auth0/signup`, {
        email: object.email,
        password: object.password,
        confirmPassword: object.confirmPassword,
        defaultCurrency: object.defaultCurrency,
      });

      if (response.data.success) {
        setSuccess('Signup successful! Please login.');
        setError(null);
        setObject({ ...object, email: '', password: '', confirmPassword: '' });

        if (!response.data.login_required && response.data.data) {
          const grant = response.data.data;
          persistAuth0Grant(grant);
          window.location.href = buildAuth0ShiftRedirectUrl(redirect_uri, grant);
        } else if (response.data.login_required) {
          setSuccess('Signup successful! Please login.');
          setError(null);
        }
      }
    } catch (err: any) {
      if (handleWrongPlatformError(err, setError)) {
        return;
      }
      console.error('Signup error:', err.response?.data);
      const errorMessage = err.response?.data?.message || 'User already exists or signup failed';
      setError(errorMessage);
    } finally {
      onLogin(false);
    }
  };

  const handleGoogleFormSubmit = () => {
    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const client_id = process.env.REACT_APP_AUTH0!;
    const redirect_uri = `${window.location.origin}/shift?defaultCurrency=${object.defaultCurrency}`;

    if (!domain || !client_id) {
      throw new Error('Auth0 configuration is missing. Please check environment variables.');
    }

    const authUrl =
      `https://${domain}/authorize?` +
      `response_type=token&` +
      `client_id=${client_id}&` +
      `redirect_uri=${encodeURIComponent(redirect_uri)}&` +
      `connection=google-oauth2&` +
      `scope=openid profile email`;

    window.location.href = authUrl;
  };
  const checkPasswordCriteria = (password: string) => {
    return {
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    };
  };

  const calculatePasswordStrength = (password: string) => {
    const criteria = checkPasswordCriteria(password);
    const fulfilledCriteria = Object.values(criteria).filter(Boolean).length;

    switch (fulfilledCriteria) {
      case 4:
        return { label: 'Strong', color: 'green' };
      case 3:
        return { label: 'Moderate', color: 'orange' };
      case 2:
        return { label: 'Weak', color: 'red' };
      default:
        return { label: 'Very Weak', color: 'darkred' };
    }
  };

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(object.password));
    setCriteria(checkPasswordCriteria(object.password));
  }, [object.password]);

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="login-modal-inner">
      <>
        <div className="login-socail">
          <button
            className="combine-button"
            style={{ cursor: 'pointer', margin: '0px auto' }}
            onClick={handleGoogleFormSubmit}
          >
            <span className="btn-svg">
              <img alt="google.svg" src="./static/google.svg" />
            </span>
            <div className="btn-text">Sign in with Google</div>
          </button>
          {/* <button className="combine-button" style={{ cursor: "pointer" }}>
              <span className="btn-svg">
                <img alt="facebook.svg" src="./static/facebook.svg" />
              </span>
              <div className="btn-text">Sign in with Facebook</div>
            </button> */}
        </div>
        <p className="login-or">Or</p>
        <div className="main-form-login">
          <label>User Name</label>
          <input
            placeholder="Username"
            value={object.username}
            onChange={e => setObject({ ...object, username: e.target.value })}
          />
          <label>Email</label>
          <input
            placeholder="Email address"
            value={object.email}
            onChange={e => setObject({ ...object, email: e.target.value })}
          />
          <label>Password</label>
          <div className="password-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={object.password}
              onChange={e => setObject({ ...object, password: e.target.value })}
            />
            <span className="password-toggle-icon" onClick={togglePasswordVisibility}>
              {showPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#007aff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#007aff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              )}
            </span>
          </div>
          <label>Confirm Password</label>
          <div className="password-input-container">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm Password"
              value={object.confirmPassword}
              onChange={e => setObject({ ...object, confirmPassword: e.target.value })}
            />
            <span className="password-toggle-icon" onClick={toggleConfirmPasswordVisibility}>
              {showConfirmPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#007aff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#007aff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              )}
            </span>
          </div>
          {object.password && (
            <div className="password-strength-section">
              <p>Your password must contain:</p>
              <ul>
                <li
                  style={{
                    color: criteria.hasLowercase ? 'green' : 'inherit',
                  }}
                >
                  Lower case letters (a-z)
                </li>
                <li
                  style={{
                    color: criteria.hasUppercase ? 'green' : 'inherit',
                  }}
                >
                  Upper case letters (A-Z)
                </li>
                <li style={{ color: criteria.hasNumber ? 'green' : 'inherit' }}>Numbers (0-9)</li>
                <li
                  style={{
                    color: criteria.hasSpecialChar ? 'green' : 'inherit',
                  }}
                >
                  Special characters (e.g. !@#$%^&*)
                </li>
              </ul>
            </div>
          )}
          {error && (
            <p className="signup-error" style={{ color: 'red' }}>
              {error}
            </p>
          )}
          {success && (
            <p className="signup-success" style={{ color: 'green' }}>
              {success}
            </p>
          )}
          <div className="form-login-inner">
            <div className="para-parent">{para}</div>
            <div className="para-parent para-parent-btn">
              <button style={{ cursor: 'pointer' }} onClick={handleFormSubmit}>
                {loader ? <div className="donut"></div> : header}
              </button>
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default Login;
